"use client";

import { type Table as HeadlessTable, flexRender } from "@tanstack/react-table";

import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { cn } from "~/lib/utils";

interface Props<TData> {
  table: HeadlessTable<TData>;
}

function Component<TData>({ table }: Props<TData>) {
  return (
    <Table>
      <Header>
        {table.getHeaderGroups().map((headerGroup) => (
          <HeaderRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <Head
                  key={header.id}
                  className={header.column.columnDef.meta?.className}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </Head>
              );
            })}
          </HeaderRow>
        ))}
      </Header>
      <Body>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <Row key={row.id} data-state={row.getIsSelected() && "selected"}>
              {row.getVisibleCells().map((cell) => (
                <Cell
                  key={cell.id}
                  className={cn(
                    cell.column.columnDef.meta?.className,
                    "first:border-l last:border-r border-b",
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Cell>
              ))}
            </Row>
          ))
        ) : (
          <Row>
            <Cell
              colSpan={table.getAllColumns().length}
              className="h-24 text-center"
            >
              No results.
            </Cell>
          </Row>
        )}
      </Body>
    </Table>
  );
}

function Table({
  children,
  className,
  ...props
}: React.ComponentProps<"table">) {
  return (
    <div className="rounded-md  xl:overflow-x-clip">
      <table
        {...props}
        className={cn(
          "w-full caption-bottom text-sm border-separate border-spacing-0",
          className,
        )}
      >
        {children}
      </table>
    </div>
  );
}

function Header({ children, ...props }: React.ComponentProps<"thead">) {
  return <TableHeader {...props}>{children}</TableHeader>;
}

function Body({ children, ...props }: React.ComponentProps<"tbody">) {
  return <TableBody {...props}>{children}</TableBody>;
}

function HeaderRow({ children, ...props }: React.ComponentProps<"tr">) {
  const fixLeftBorder =
    "[&>th:last-child:after]:block [&>th:last-child:after]:content-[''] [&>th:last-child:after]:-top-1 [&>th:last-child:after]:w-1 [&>th:last-child:after]:absolute [&>th:last-child:after]:bg-background [&>th:last-child:after]:-right-1 [&>th:last-child:after]:h-2";
  const fixRightBorder =
    "[&>th:first-child:before]:block [&>th:first-child:before]:content-[''] [&>th:first-child:before]:-top-1 [&>th:first-child:before]:w-1 [&>th:first-child:before]:absolute [&>th:first-child:before]:bg-background [&>th:first-child:before]:-left-1 [&>th:first-child:before]:h-2";

  return (
    <TableRow
      {...props}
      className={cn(props.className, `${fixLeftBorder} ${fixRightBorder}`)}
    >
      {children}
    </TableRow>
  );
}

function Head({ children, ...props }: React.ComponentProps<"th">) {
  return (
    <TableHead
      {...props}
      className={cn(
        props.className,
        "sticky top-0 bg-background z-20 border-y last:border-r first:border-l first:rounded-tl-md last:rounded-tr-md",
      )}
    >
      {children}
    </TableHead>
  );
}

function SubHead({ children, ...props }: React.ComponentProps<"th">) {
  return (
    <TableHead
      {...props}
      className={cn(
        props.className,
        "bg-muted/50 text-muted-foreground border-b first:border-l last:border-r",
      )}
    >
      {children}
    </TableHead>
  );
}

function Row({
  children,
  noRounding,
  ...props
}: React.ComponentProps<"tr"> & { noRounding?: boolean }) {
  return (
    <TableRow
      {...props}
      className={cn(
        !noRounding &&
          "last:[&>td:first-child]:rounded-bl-md last:[&>td:last-child]:rounded-br-md",
        props.className,
      )}
    >
      {children}
    </TableRow>
  );
}

function Cell({ children, ...props }: React.ComponentProps<"td">) {
  return (
    <TableCell
      {...props}
      className={cn(props.className, "first:border-l last:border-r border-b")}
    >
      {children}
    </TableCell>
  );
}

export const DataTable = Object.assign(Component, {
  Table,
  Header,
  Body,
  HeaderRow,
  Head,
  SubHead,
  Row,
  Cell,
});
